import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Send,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Mail,
  Users,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Megaphone,
  Filter,
  DollarSign,
  BarChart3
} from "lucide-react";
import {
  fetchZipCodeStats,
  createCampaign,
  sendCampaign,
  POSTCARD_SIZES,
  formatCurrency
} from "../../services/MailingListService";

/**
 * CreateCampaignModal - Multi-step wizard for creating and sending campaigns
 *
 * Steps:
 * 1. Select Contacts - Filter by ZIP code, view counts
 * 2. Configure Card - Choose size, template (or use provided postcardDesign)
 * 3. Review & Send - Preview costs, confirm
 *
 * Props:
 * - listId: Mailing list ID
 * - list: Mailing list object
 * - onClose: Close handler
 * - onSent: Success handler
 * - postcardDesign: Optional { front_html, back_html, template_id, size, photoUrl } from PropertyPostcardBuilderModal
 */
const CreateCampaignModal = ({ listId, list, onClose, onSent, postcardDesign }) => {
  // Wizard state
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Contact selection
  const [zipStats, setZipStats] = useState(null);
  const [selectedZips, setSelectedZips] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);

  // Step 2: Card configuration
  const [size, setSize] = useState(postcardDesign?.size || "4x6");
  const [campaignName, setCampaignName] = useState("");

  // If postcardDesign is provided, we have HTML ready
  const hasPostcardDesign = !!postcardDesign?.front_html;

  // Step 3: Sending
  const [campaign, setCampaign] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Load ZIP code stats on mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoading(true);
        const stats = await fetchZipCodeStats(listId);
        setZipStats(stats);

        // Default campaign name
        setCampaignName(`${list?.name || "Campaign"} - ${new Date().toLocaleDateString()}`);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadStats();
  }, [listId, list?.name]);

  // Calculate selected contact count
  const selectedContactCount = useMemo(() => {
    if (!zipStats) return 0;
    if (selectAll) return zipStats.total_contacts;

    return zipStats.zip_codes
      .filter(z => selectedZips.has(z.zip_code))
      .reduce((sum, z) => sum + z.contact_count, 0);
  }, [zipStats, selectAll, selectedZips]);

  // Calculate cost
  const pricePerCard = POSTCARD_SIZES.find(s => s.id === size)?.price || 0.70;
  const totalCost = selectedContactCount * pricePerCard;
  const creditsNeeded = selectedContactCount * 600;

  // Toggle ZIP selection
  const toggleZip = (zipCode) => {
    const newSet = new Set(selectedZips);
    if (newSet.has(zipCode)) {
      newSet.delete(zipCode);
    } else {
      newSet.add(zipCode);
    }
    setSelectedZips(newSet);
    setSelectAll(false);
  };

  // Handle next step
  const handleNext = async () => {
    setError(null);

    if (step === 1) {
      if (selectedContactCount === 0) {
        setError("Please select at least one ZIP code");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!campaignName.trim()) {
        setError("Please enter a campaign name");
        return;
      }

      // Create campaign
      try {
        setIsLoading(true);

        const selection = selectAll
          ? { mode: "all" }
          : {
              mode: "filtered",
              zip_filter: {
                zip_codes: Array.from(selectedZips),
                mode: "include"
              }
            };

        const campaignPayload = {
          name: campaignName.trim(),
          list_id: listId,
          selection,
          postcard_size: size
        };

        // Include postcard design HTML if provided (from PropertyPostcardBuilderModal)
        if (postcardDesign?.front_html) {
          campaignPayload.front_html = postcardDesign.front_html;
          campaignPayload.back_html = postcardDesign.back_html;
          campaignPayload.template_id = postcardDesign.template_id;
        }

        const newCampaign = await createCampaign(campaignPayload);

        setCampaign(newCampaign);
        setStep(3);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle send
  const handleSend = async (dryRun = false) => {
    if (!dryRun && !confirmed) {
      setError("Please confirm the campaign details");
      return;
    }

    try {
      setIsSending(true);
      setError(null);

      const result = await sendCampaign(campaign.id, { dry_run: dryRun });
      setSendResult(result);

      if (!dryRun && result.sent > 0) {
        setTimeout(() => onSent(), 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
              s === step
                ? "bg-sky-600 text-white"
                : s < step
                ? "bg-green-500 text-white"
                : "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
            }`}
          >
            {s < step ? <CheckCircle className="w-4 h-4" /> : s}
          </div>
          {s < 3 && (
            <div
              className={`w-12 h-0.5 mx-1 ${
                s < step
                  ? "bg-green-500"
                  : "bg-slate-200 dark:bg-slate-700"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  // Step titles
  const stepTitles = {
    1: "Select Contacts",
    2: "Configure Campaign",
    3: "Review & Send"
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 dark:bg-slate-950/80 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold dark:text-white text-slate-900">Create Campaign</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">{stepTitles[step]}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSending}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Loading */}
          {isLoading && !sendResult && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Step Indicator */}
          {!isLoading && !sendResult && <StepIndicator />}

          {/* Step 1: Select Contacts */}
          {step === 1 && !isLoading && zipStats && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold dark:text-white text-slate-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-sky-600" />
                    Select by ZIP Code
                  </h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Choose which ZIP codes to target
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => {
                      setSelectAll(e.target.checked);
                      if (e.target.checked) setSelectedZips(new Set());
                    }}
                    className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Select All</span>
                </label>
              </div>

              {/* ZIP code grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-auto">
                {zipStats.zip_codes.map((zip) => {
                  const isSelected = selectAll || selectedZips.has(zip.zip_code);
                  return (
                    <button
                      key={zip.zip_code}
                      onClick={() => toggleZip(zip.zip_code)}
                      disabled={selectAll}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        isSelected
                          ? "border-sky-500 bg-sky-50 dark:bg-sky-900/20"
                          : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                      } ${selectAll ? "opacity-70" : ""}`}
                    >
                      <div className="font-mono font-bold dark:text-white text-slate-900">
                        {zip.zip_code}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {zip.city || "—"}
                      </div>
                      <div className="text-sm font-semibold text-sky-600 dark:text-sky-400">
                        {zip.contact_count.toLocaleString()} contacts
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Summary */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-sky-600" />
                  <span className="font-medium dark:text-white text-slate-900">
                    {selectedContactCount.toLocaleString()} contacts selected
                  </span>
                </div>
                <span className="text-slate-500 dark:text-slate-400">
                  {selectAll ? "All ZIP codes" : `${selectedZips.size} ZIP codes`}
                </span>
              </div>
            </div>
          )}

          {/* Step 2: Configure Campaign */}
          {step === 2 && !isLoading && (
            <div className="space-y-6">
              {/* Campaign name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Campaign Name
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g., April 2026 - Dallas 75201"
                  className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>

              {/* Postcard preview if design provided */}
              {hasPostcardDesign && postcardDesign?.photoUrl && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-medium text-emerald-700 dark:text-emerald-300">
                      Custom Postcard Design Ready
                    </span>
                  </div>
                  <div className="flex gap-4">
                    <img
                      src={postcardDesign.photoUrl}
                      alt="Postcard preview"
                      className="w-24 h-16 object-cover rounded-lg border border-emerald-200 dark:border-emerald-700"
                    />
                    <div className="text-sm text-emerald-600 dark:text-emerald-400">
                      <p>Template: {postcardDesign.template_id || "Custom"}</p>
                      <p>Size: {postcardDesign.size || "4x6"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Postcard size */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Postcard Size
                </label>
                <div className="flex gap-3">
                  {POSTCARD_SIZES.map((sizeOption) => {
                    const isSelected = size === sizeOption.id;
                    return (
                      <button
                        key={sizeOption.id}
                        onClick={() => setSize(sizeOption.id)}
                        className={`flex-1 p-4 border rounded-xl text-left transition-all ${
                          isSelected
                            ? "border-sky-500 bg-sky-50 dark:bg-sky-900/20 ring-2 ring-sky-500/20"
                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                        }`}
                      >
                        <div className="font-bold dark:text-white text-slate-900">{sizeOption.name}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{sizeOption.description}</div>
                        <div className="text-lg font-semibold text-sky-600 dark:text-sky-400 mt-1">
                          {formatCurrency(sizeOption.price)}/card
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Cost preview */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Selected contacts</span>
                  <span className="font-medium dark:text-white text-slate-900">
                    {selectedContactCount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Price per postcard</span>
                  <span className="font-medium dark:text-white text-slate-900">
                    {formatCurrency(pricePerCard)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                  <span className="font-semibold dark:text-white text-slate-900">Estimated Total</span>
                  <span className="font-bold text-lg text-sky-600 dark:text-sky-400">
                    {formatCurrency(totalCost)}
                  </span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 text-center pt-1">
                  {creditsNeeded.toLocaleString()} credits will be used
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review & Send */}
          {step === 3 && !sendResult && campaign && (
            <div className="space-y-6">
              {/* Campaign summary */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-3">
                <h4 className="font-semibold dark:text-white text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-sky-600" />
                  Campaign Summary
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Name</div>
                    <div className="font-medium dark:text-white text-slate-900">{campaign.name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Size</div>
                    <div className="font-medium dark:text-white text-slate-900">{campaign.postcard_size}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Contacts</div>
                    <div className="font-medium dark:text-white text-slate-900">
                      {campaign.metrics?.total_contacts?.toLocaleString() || selectedContactCount.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Est. Cost</div>
                    <div className="font-bold text-sky-600 dark:text-sky-400">
                      {formatCurrency(campaign.metrics?.estimated_cost || totalCost)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation */}
              <label className="flex items-start gap-3 cursor-pointer p-4 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-sky-300 dark:hover:border-sky-700 transition-colors">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={isSending}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  I understand this will send{" "}
                  <strong className="text-slate-900 dark:text-white">
                    {campaign.metrics?.total_contacts?.toLocaleString() || selectedContactCount.toLocaleString()} postcards
                  </strong>{" "}
                  via USPS and charge{" "}
                  <strong className="text-sky-600 dark:text-sky-400">
                    {formatCurrency(campaign.metrics?.estimated_cost || totalCost)}
                  </strong>{" "}
                  to my account. This action cannot be undone.
                </span>
              </label>
            </div>
          )}

          {/* Success Result */}
          {sendResult && !sendResult.dry_run && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                Campaign Sent Successfully!
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {sendResult.sent.toLocaleString()} postcards have been queued for delivery.
                {sendResult.failed > 0 && ` ${sendResult.failed} failed to send.`}
              </p>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl inline-block">
                <div className="text-sm text-slate-500 dark:text-slate-400">Total Charged</div>
                <div className="text-2xl font-bold text-sky-600 dark:text-sky-400">
                  {formatCurrency(sendResult.estimated_cost)}
                </div>
              </div>
            </div>
          )}

          {/* Dry Run Preview */}
          {sendResult && sendResult.dry_run && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium mb-2">
                <Mail className="w-5 h-5" />
                Preview Mode
              </div>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                {sendResult.sent} postcards would be sent.
                {sendResult.failed > 0 && ` ${sendResult.failed} would fail.`}
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                Estimated cost: {formatCurrency(sendResult.estimated_cost)}
              </p>
              <p className="text-xs text-amber-500 mt-2">
                No postcards were sent. Click "Send Campaign" to proceed.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-slate-200 dark:border-slate-800">
          {/* Back button */}
          {step > 1 && !sendResult && (
            <button
              onClick={() => setStep(step - 1)}
              disabled={isLoading || isSending}
              className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}

          {/* Cancel/Close */}
          <button
            onClick={onClose}
            disabled={isSending}
            className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {sendResult && !sendResult.dry_run ? "Close" : "Cancel"}
          </button>

          <div className="flex-1" />

          {/* Next/Send buttons */}
          {step < 3 && (
            <button
              onClick={handleNext}
              disabled={isLoading || selectedContactCount === 0}
              className="px-6 py-2.5 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-lg font-bold transition-colors shadow-lg shadow-sky-900/20 disabled:shadow-none flex items-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          )}

          {step === 3 && (!sendResult || sendResult.dry_run) && (
            <>
              <button
                onClick={() => handleSend(true)}
                disabled={isSending}
                className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSending && sendResult?.dry_run ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                Preview
              </button>
              <button
                onClick={() => handleSend(false)}
                disabled={isSending || !confirmed}
                className="px-6 py-2.5 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-lg font-bold transition-colors shadow-lg shadow-sky-900/20 disabled:shadow-none flex items-center gap-2"
              >
                {isSending && !sendResult?.dry_run ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Campaign
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignModal;
